import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '@/i18n';

/**
 * Language Store
 * 
 * Manages the current language state and provides language switching functionality
 */

export type SupportedLanguage = 'pt' | 'en';

export interface LanguageState {
  currentLanguage: SupportedLanguage;
  isLoading: boolean;
}

export interface LanguageActions {
  setLanguage: (language: SupportedLanguage) => Promise<void>;
  toggleLanguage: () => Promise<void>;
  initializeLanguage: () => Promise<void>;
}

interface LanguageStore extends LanguageState, LanguageActions {}

export const useLanguageStore = create<LanguageStore>()(
  persist(
    (set, get) => ({
      // State
      currentLanguage: 'pt',
      isLoading: false,

      // Actions
      setLanguage: async (language: SupportedLanguage) => {
        try {
          set({ isLoading: true });
          await i18n.changeLanguage(language);
          set({ 
            currentLanguage: language,
            isLoading: false 
          });
        } catch (error) {
          console.error('Failed to change language:', error);
          set({ isLoading: false });
        }
      },

      toggleLanguage: async () => {
        const { currentLanguage, setLanguage } = get();
        const newLanguage: SupportedLanguage = currentLanguage === 'pt' ? 'en' : 'pt';
        await setLanguage(newLanguage);
      },

      initializeLanguage: async () => {
        try {
          set({ isLoading: true });
          const savedLanguage = localStorage.getItem('alz-language') as SupportedLanguage;
          const detectedLanguage = i18n.language as SupportedLanguage;
          
          // Priority: saved > detected > default (pt)
          const languageToUse = savedLanguage || detectedLanguage || 'pt';
          
          await i18n.changeLanguage(languageToUse);
          set({ 
            currentLanguage: languageToUse,
            isLoading: false 
          });
        } catch (error) {
          console.error('Failed to initialize language:', error);
          set({ 
            currentLanguage: 'pt',
            isLoading: false 
          });
        }
      },
    }),
    {
      name: 'alz-language-storage',
      partialize: (state) => ({
        currentLanguage: state.currentLanguage,
      }),
    }
  )
);

// Selector hooks for better performance
export const useCurrentLanguage = () => useLanguageStore((state) => state.currentLanguage);
export const useLanguageActions = () => useLanguageStore((state) => ({
  setLanguage: state.setLanguage,
  toggleLanguage: state.toggleLanguage,
  initializeLanguage: state.initializeLanguage,
}));
export const useLanguageLoading = () => useLanguageStore((state) => state.isLoading);